import { api } from "@/services/auth";

export type WorkflowModuleKey = "crm" | "ops";
export type WorkflowBadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "danger"
  | "primary"
  | "muted";

export type WorkflowStatus = {
  key: string;
  label: string;
  order: number;
  color: string;
  bgColor: string;
  borderColor: string;
  kind: string;
  isTerminal: boolean;
  countsAsActive: boolean;
  countsAsSuccess: boolean;
  countsAsLoss: boolean;
  pauseSla: boolean;
};

export type WorkflowColumn = {
  key: string;
  label: string;
  order: number;
  statusKeys: string[];
  color: string;
  bgColor: string;
  dotColor: string;
};

export type WorkflowSwimlane = {
  key: string;
  label: string;
  type: string;
  enabled: boolean;
  default: boolean;
};

export type WorkflowMetricRule = {
  key: string;
  label: string;
  aggregation: "count";
  statusKeys: string[];
  color: string;
};

export type WorkflowConfig = {
  moduleKey: WorkflowModuleKey;
  entityType: string;
  statuses: WorkflowStatus[];
  columns: WorkflowColumn[];
  swimlanes: WorkflowSwimlane[];
  metricRules: WorkflowMetricRule[];
};

type KanbanColumnConfig = {
  id: string;
  title: string;
  color?: string;
  bgColor?: string;
  dotColor?: string;
};

type PipelineStageItem = { title: string; client?: string };

export type CrmClientWorkflowStageKey = "lead" | "em-negociacao" | "ganho" | "perdido";

export type CrmClientWorkflowStage = {
  id: CrmClientWorkflowStageKey;
  label: string;
  href: string;
  variant: string;
  bg: string;
  color: string;
  borderColor: string;
  value: number;
  items?: PipelineStageItem[];
};

type CrmClientWorkflowStageInput = Partial<
  Pick<CrmClientWorkflowStage, "label" | "href" | "value" | "items">
>;

type CrmClientWorkflowSource = {
  stagesBefore?: CrmClientWorkflowStageInput[];
  stages?: CrmClientWorkflowStageInput[];
  parallelStages?: CrmClientWorkflowStageInput[];
  stagesAfter?: CrmClientWorkflowStageInput[];
  finalStages?: CrmClientWorkflowStageInput[];
};

export type OpsDemandPriorityKey =
  | "urgent"
  | "critica"
  | "high"
  | "alta"
  | "medium"
  | "media"
  | "low"
  | "baixa";

type OpsDemandPriorityMeta = {
  key: OpsDemandPriorityKey;
  label: string;
  badgeVariant: WorkflowBadgeVariant;
  dot: string;
  bg: string;
  border: string;
  level: number;
  aliases: string[];
};

const CRM_CLIENT_STAGE_ORDER: CrmClientWorkflowStageKey[] = [
  "lead",
  "em-negociacao",
  "ganho",
  "perdido",
];

const CRM_DEFAULT_STATUSES: WorkflowStatus[] = [
  {
    key: "lead",
    label: "Atendimento",
    order: 1,
    color: "#1B3A5C",
    bgColor: "rgba(27,58,92,0.06)",
    borderColor: "rgba(27,58,92,0.16)",
    kind: "open",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "qualificacao",
    label: "Qualificação",
    order: 2,
    color: "#E8721A",
    bgColor: "rgba(232,114,26,0.08)",
    borderColor: "rgba(232,114,26,0.18)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "proposta",
    label: "Proposta",
    order: 3,
    color: "#E8721A",
    bgColor: "rgba(232,114,26,0.08)",
    borderColor: "rgba(232,114,26,0.18)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "negociacao",
    label: "Negociação",
    order: 4,
    color: "#E8721A",
    bgColor: "rgba(232,114,26,0.08)",
    borderColor: "rgba(232,114,26,0.18)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "ganho",
    label: "Negócios Fechados",
    order: 5,
    color: "#0F8A5F",
    bgColor: "rgba(15,138,95,0.08)",
    borderColor: "rgba(15,138,95,0.18)",
    kind: "won",
    isTerminal: true,
    countsAsActive: false,
    countsAsSuccess: true,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "perdido",
    label: "Negócios Perdidos",
    order: 6,
    color: "#C2415D",
    bgColor: "rgba(194,65,93,0.08)",
    borderColor: "rgba(194,65,93,0.18)",
    kind: "lost",
    isTerminal: true,
    countsAsActive: false,
    countsAsSuccess: false,
    countsAsLoss: true,
    pauseSla: false,
  },
];

const CRM_DEFAULT_COLUMNS: WorkflowColumn[] = [
  {
    key: "lead",
    label: "Atendimento",
    order: 1,
    statusKeys: ["lead"],
    color: "#1B3A5C",
    bgColor: "bg-slate-100",
    dotColor: "bg-slate-500",
  },
  {
    key: "em-negociacao",
    label: "Em Negociação",
    order: 2,
    statusKeys: ["qualificacao", "proposta", "negociacao"],
    color: "#E8721A",
    bgColor: "bg-amber-100",
    dotColor: "bg-amber-500",
  },
  {
    key: "ganho",
    label: "Negócios Fechados",
    order: 3,
    statusKeys: ["ganho"],
    color: "#0F8A5F",
    bgColor: "bg-emerald-100",
    dotColor: "bg-emerald-500",
  },
  {
    key: "perdido",
    label: "Negócios Perdidos",
    order: 4,
    statusKeys: ["perdido"],
    color: "#C2415D",
    bgColor: "bg-rose-100",
    dotColor: "bg-rose-500",
  },
];

const OPS_DEFAULT_STATUSES: WorkflowStatus[] = [
  {
    key: "novo",
    label: "Novo",
    order: 1,
    color: "#3b82f6",
    bgColor: "rgba(59,130,246,0.08)",
    borderColor: "rgba(59,130,246,0.16)",
    kind: "open",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "triagem",
    label: "Triagem",
    order: 2,
    color: "#8b5cf6",
    bgColor: "rgba(139,92,246,0.08)",
    borderColor: "rgba(139,92,246,0.16)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "analise",
    label: "Em análise",
    order: 3,
    color: "#f59e0b",
    bgColor: "rgba(245,158,11,0.08)",
    borderColor: "rgba(245,158,11,0.18)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "execucao",
    label: "Execução",
    order: 4,
    color: "#10b981",
    bgColor: "rgba(16,185,129,0.08)",
    borderColor: "rgba(16,185,129,0.18)",
    kind: "in_progress",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "aguardando_cliente",
    label: "Aguardando cliente",
    order: 5,
    color: "#f97316",
    bgColor: "rgba(249,115,22,0.08)",
    borderColor: "rgba(249,115,22,0.18)",
    kind: "waiting",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: true,
  },
  {
    key: "aguardando_terceiro",
    label: "Aguardando terceiro",
    order: 6,
    color: "#f97316",
    bgColor: "rgba(249,115,22,0.08)",
    borderColor: "rgba(249,115,22,0.18)",
    kind: "waiting",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: true,
  },
  {
    key: "bloqueado",
    label: "Bloqueado",
    order: 7,
    color: "#ef4444",
    bgColor: "rgba(239,68,68,0.08)",
    borderColor: "rgba(239,68,68,0.18)",
    kind: "blocked",
    isTerminal: false,
    countsAsActive: true,
    countsAsSuccess: false,
    countsAsLoss: false,
    pauseSla: true,
  },
  {
    key: "concluido",
    label: "Concluído",
    order: 8,
    color: "#10b981",
    bgColor: "rgba(16,185,129,0.08)",
    borderColor: "rgba(16,185,129,0.18)",
    kind: "done",
    isTerminal: true,
    countsAsActive: false,
    countsAsSuccess: true,
    countsAsLoss: false,
    pauseSla: false,
  },
  {
    key: "cancelado",
    label: "Cancelado",
    order: 9,
    color: "#94a3b8",
    bgColor: "rgba(148,163,184,0.08)",
    borderColor: "rgba(148,163,184,0.18)",
    kind: "lost",
    isTerminal: true,
    countsAsActive: false,
    countsAsSuccess: false,
    countsAsLoss: true,
    pauseSla: false,
  },
];

function buildColumnsFromStatuses(statuses: WorkflowStatus[]): WorkflowColumn[] {
  return statuses.map((status) => ({
    key: status.key,
    label: status.label,
    order: status.order,
    statusKeys: [status.key],
    color: status.color,
    bgColor:
      status.kind === "waiting"
        ? "bg-amber-100"
        : status.kind === "blocked" || status.kind === "lost"
          ? "bg-rose-100"
          : status.kind === "done" || status.kind === "won"
            ? "bg-emerald-100"
            : status.kind === "open"
              ? "bg-blue-100"
              : "bg-slate-100",
    dotColor:
      status.kind === "waiting"
        ? "bg-amber-500"
        : status.kind === "blocked" || status.kind === "lost"
          ? "bg-rose-500"
          : status.kind === "done" || status.kind === "won"
            ? "bg-emerald-500"
            : status.kind === "open"
              ? "bg-blue-500"
              : "bg-slate-500",
  }));
}

const OPS_DEFAULT_COLUMNS = buildColumnsFromStatuses(OPS_DEFAULT_STATUSES);

const DEFAULT_WORKFLOW_CONFIGS: Record<WorkflowModuleKey, WorkflowConfig> = {
  crm: {
    moduleKey: "crm",
    entityType: "opportunity",
    statuses: CRM_DEFAULT_STATUSES,
    columns: CRM_DEFAULT_COLUMNS,
    swimlanes: [
      { key: "responsavel", label: "Responsável", type: "assignee", enabled: true, default: true },
      { key: "modulo", label: "Módulo", type: "module", enabled: true, default: false },
      { key: "origem", label: "Origem", type: "source", enabled: true, default: false },
      { key: "ano", label: "Ano", type: "year", enabled: true, default: false },
    ],
    metricRules: [
      { key: "ativos", label: "Atendimentos Ativos", aggregation: "count", statusKeys: ["lead", "qualificacao", "proposta", "negociacao"], color: "#1B3A5C" },
      { key: "ganhos", label: "Negócios Fechados", aggregation: "count", statusKeys: ["ganho"], color: "#0F8A5F" },
      { key: "perdidos", label: "Negócios Perdidos", aggregation: "count", statusKeys: ["perdido"], color: "#C2415D" },
    ],
  },
  ops: {
    moduleKey: "ops",
    entityType: "demand",
    statuses: OPS_DEFAULT_STATUSES,
    columns: OPS_DEFAULT_COLUMNS,
    swimlanes: [
      { key: "responsavel", label: "Responsável", type: "assignee", enabled: true, default: true },
      { key: "cliente", label: "Cliente", type: "client", enabled: true, default: false },
      { key: "prioridade", label: "Prioridade", type: "priority", enabled: true, default: false },
      { key: "ano", label: "Ano", type: "year", enabled: true, default: false },
    ],
    metricRules: [
      { key: "ativas", label: "Demandas Ativas", aggregation: "count", statusKeys: ["novo", "triagem", "analise", "execucao", "aguardando_cliente", "aguardando_terceiro", "bloqueado"], color: "#3b82f6" },
      { key: "concluidas", label: "Concluídas", aggregation: "count", statusKeys: ["concluido"], color: "#10b981" },
      { key: "canceladas", label: "Canceladas", aggregation: "count", statusKeys: ["cancelado"], color: "#94a3b8" },
    ],
  },
};

const OPS_STATUS_ALIASES: Record<string, string> = {
  nova: "novo",
  cliente: "aguardando_cliente",
  "aguardando-cliente": "aguardando_cliente",
  aguardando: "aguardando_cliente",
  andamento: "execucao",
  "em-andamento": "execucao",
  "em-analise": "analise",
  concluida: "concluido",
  concluido: "concluido",
  finalizado: "concluido",
  cancelada: "cancelado",
};

const OPS_PRIORITY_CONFIG: Record<OpsDemandPriorityKey, OpsDemandPriorityMeta> = {
  urgent: {
    key: "urgent",
    label: "Urgente",
    badgeVariant: "danger",
    dot: "#ef4444",
    bg: "rgba(239,68,68,0.08)",
    border: "rgba(239,68,68,0.2)",
    level: 4,
    aliases: ["urgent"],
  },
  critica: {
    key: "critica",
    label: "Crítica",
    badgeVariant: "danger",
    dot: "#ef4444",
    bg: "rgba(239,68,68,0.08)",
    border: "rgba(239,68,68,0.2)",
    level: 4,
    aliases: ["critica", "crítica"],
  },
  high: {
    key: "high",
    label: "Alta",
    badgeVariant: "warning",
    dot: "#f59e0b",
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.2)",
    level: 3,
    aliases: ["high"],
  },
  alta: {
    key: "alta",
    label: "Alta",
    badgeVariant: "warning",
    dot: "#f59e0b",
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.2)",
    level: 3,
    aliases: ["alta"],
  },
  medium: {
    key: "medium",
    label: "Média",
    badgeVariant: "default",
    dot: "#3b82f6",
    bg: "rgba(59,130,246,0.08)",
    border: "rgba(59,130,246,0.15)",
    level: 2,
    aliases: ["medium"],
  },
  media: {
    key: "media",
    label: "Média",
    badgeVariant: "default",
    dot: "#3b82f6",
    bg: "rgba(59,130,246,0.08)",
    border: "rgba(59,130,246,0.15)",
    level: 2,
    aliases: ["media", "média"],
  },
  low: {
    key: "low",
    label: "Baixa",
    badgeVariant: "muted",
    dot: "#94a3b8",
    bg: "rgba(148,163,184,0.06)",
    border: "rgba(148,163,184,0.12)",
    level: 1,
    aliases: ["low"],
  },
  baixa: {
    key: "baixa",
    label: "Baixa",
    badgeVariant: "muted",
    dot: "#94a3b8",
    bg: "rgba(148,163,184,0.06)",
    border: "rgba(148,163,184,0.12)",
    level: 1,
    aliases: ["baixa"],
  },
};

function sortByOrder<T extends { order?: number }>(items: T[]) {
  return [...items].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

function normalizeWorkflowText(value?: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\s]+/g, "-");
}

function cloneWorkflowConfig(config: WorkflowConfig): WorkflowConfig {
  return {
    ...config,
    statuses: config.statuses.map((status) => ({ ...status })),
    columns: config.columns.map((column) => ({ ...column, statusKeys: [...column.statusKeys] })),
    swimlanes: config.swimlanes.map((swimlane) => ({ ...swimlane })),
    metricRules: config.metricRules.map((rule) => ({ ...rule, statusKeys: [...rule.statusKeys] })),
  };
}

export function getDefaultWorkflowConfig(moduleKey: WorkflowModuleKey): WorkflowConfig {
  return cloneWorkflowConfig(DEFAULT_WORKFLOW_CONFIGS[moduleKey]);
}

export function adaptWorkflowConfig(payload: unknown, fallbackModuleKey: WorkflowModuleKey = "crm"): WorkflowConfig {
  const fallback = getDefaultWorkflowConfig(fallbackModuleKey);
  const obj = (payload && typeof payload === "object" ? payload : {}) as Partial<WorkflowConfig>;
  const moduleKey = obj.moduleKey === "ops" ? "ops" : obj.moduleKey === "crm" ? "crm" : fallback.moduleKey;

  return {
    moduleKey,
    entityType: String(obj.entityType || fallback.entityType),
    statuses: Array.isArray(obj.statuses)
      ? sortByOrder(
          obj.statuses.map((status) => ({
            key: String(status?.key || ""),
            label: String(status?.label || status?.key || ""),
            order: Number(status?.order || 0),
            color: String(status?.color || ""),
            bgColor: String(status?.bgColor || ""),
            borderColor: String(status?.borderColor || ""),
            kind: String(status?.kind || "open"),
            isTerminal: Boolean(status?.isTerminal),
            countsAsActive: Boolean(status?.countsAsActive),
            countsAsSuccess: Boolean(status?.countsAsSuccess),
            countsAsLoss: Boolean(status?.countsAsLoss),
            pauseSla: Boolean(status?.pauseSla),
          }))
        ).filter((status) => status.key)
      : fallback.statuses,
    columns: Array.isArray(obj.columns)
      ? sortByOrder(
          obj.columns.map((column) => ({
            key: String(column?.key || ""),
            label: String(column?.label || column?.key || ""),
            order: Number(column?.order || 0),
            statusKeys: Array.isArray(column?.statusKeys)
              ? column.statusKeys.map((statusKey) => String(statusKey)).filter(Boolean)
              : [],
            color: String(column?.color || ""),
            bgColor: String(column?.bgColor || ""),
            dotColor: String(column?.dotColor || ""),
          }))
        ).filter((column) => column.key)
      : fallback.columns,
    swimlanes: Array.isArray(obj.swimlanes)
      ? obj.swimlanes.map((swimlane) => ({
          key: String(swimlane?.key || ""),
          label: String(swimlane?.label || swimlane?.key || ""),
          type: String(swimlane?.type || ""),
          enabled: Boolean(swimlane?.enabled),
          default: Boolean(swimlane?.default),
        })).filter((swimlane) => swimlane.key)
      : fallback.swimlanes,
    metricRules: Array.isArray(obj.metricRules)
      ? obj.metricRules.map((rule) => ({
          key: String(rule?.key || ""),
          label: String(rule?.label || rule?.key || ""),
          aggregation: "count" as const,
          statusKeys: Array.isArray(rule?.statusKeys)
            ? rule.statusKeys.map((statusKey) => String(statusKey)).filter(Boolean)
            : [],
          color: String(rule?.color || ""),
        })).filter((rule) => rule.key)
      : fallback.metricRules,
  };
}

function statusKindToBadgeVariant(kind?: string): WorkflowBadgeVariant {
  if (kind === "won" || kind === "done") return "success";
  if (kind === "waiting") return "warning";
  if (kind === "lost" || kind === "blocked") return "danger";
  if (kind === "open") return "primary";
  if (kind === "in_progress") return "default";
  return "muted";
}

function resolveWorkflowStatus(
  config: WorkflowConfig,
  status?: string,
  aliasMap?: Record<string, string>,
): WorkflowStatus | undefined {
  const normalized = normalizeWorkflowText(status);
  if (!normalized) return undefined;

  const exact = config.statuses.find(
    (item) =>
      normalizeWorkflowText(item.key) === normalized ||
      normalizeWorkflowText(item.label) === normalized,
  );
  if (exact) return exact;

  const aliasTarget = aliasMap?.[normalized];
  if (!aliasTarget) return undefined;
  return config.statuses.find((item) => item.key === aliasTarget);
}

function getCrmColumnByKey(
  key: CrmClientWorkflowStageKey,
  config: WorkflowConfig = getDefaultWorkflowConfig("crm"),
) {
  return (
    config.columns.find((column) => column.key === key) ??
    getDefaultWorkflowConfig("crm").columns.find((column) => column.key === key)!
  );
}

function getCrmStageVariant(key: CrmClientWorkflowStageKey) {
  if (key === "ganho") return "bg-success/10 text-success";
  if (key === "perdido") return "bg-danger/10 text-danger";
  if (key === "em-negociacao") return "bg-primary/10 text-primary";
  return "bg-muted text-foreground";
}

export function getWorkflowKanbanColumns(
  moduleKey: WorkflowModuleKey,
  config = getDefaultWorkflowConfig(moduleKey),
): KanbanColumnConfig[] {
  const workflowConfig = config.moduleKey === moduleKey ? config : getDefaultWorkflowConfig(moduleKey);
  const columns = workflowConfig.columns.length > 0 ? workflowConfig.columns : buildColumnsFromStatuses(workflowConfig.statuses);

  return columns.map((column) => ({
    id: column.key,
    title: column.label,
    color: "text-slate-700",
    bgColor: column.bgColor || "bg-slate-100",
    dotColor: column.dotColor || "bg-slate-500",
  }));
}

export function getCrmKanbanColumns(config = getDefaultWorkflowConfig("crm")) {
  return getWorkflowKanbanColumns("crm", config);
}

export function getOpsKanbanColumns(config = getDefaultWorkflowConfig("ops")) {
  return getWorkflowKanbanColumns("ops", config);
}

export function getCrmClientWorkflowStageMeta(
  key: CrmClientWorkflowStageKey,
  config = getDefaultWorkflowConfig("crm"),
): Omit<CrmClientWorkflowStage, "value" | "items"> {
  const column = getCrmColumnByKey(key, config);
  return {
    id: key,
    label: column.label,
    href: `/comercial/atendimentos?etapa=${column.key}`,
    variant: getCrmStageVariant(key),
    bg:
      key === "lead"
        ? "rgba(27,58,92,0.06)"
        : key === "em-negociacao"
          ? "rgba(232,114,26,0.08)"
          : key === "ganho"
            ? "rgba(15,138,95,0.08)"
            : "rgba(194,65,93,0.08)",
    color: column.color,
    borderColor:
      key === "lead"
        ? "rgba(27,58,92,0.16)"
        : key === "em-negociacao"
          ? "rgba(232,114,26,0.18)"
          : key === "ganho"
            ? "rgba(15,138,95,0.18)"
            : "rgba(194,65,93,0.18)",
  };
}

export function createCrmClientWorkflowStage(
  key: CrmClientWorkflowStageKey,
  value = 0,
  config = getDefaultWorkflowConfig("crm"),
): CrmClientWorkflowStage {
  return {
    ...getCrmClientWorkflowStageMeta(key, config),
    value,
    items: [],
  };
}

export function resolveCrmClientWorkflowStageKey(
  stage?: Pick<CrmClientWorkflowStageInput, "label" | "href"> | null,
  config = getDefaultWorkflowConfig("crm"),
): CrmClientWorkflowStageKey {
  const href = normalizeWorkflowText(stage?.href);
  const hrefMatch = config.columns.find(
    (column) =>
      href.includes(`etapa=${normalizeWorkflowText(column.key)}`) ||
      href.includes(normalizeWorkflowText(column.key)),
  );
  if (hrefMatch && CRM_CLIENT_STAGE_ORDER.includes(hrefMatch.key as CrmClientWorkflowStageKey)) {
    return hrefMatch.key as CrmClientWorkflowStageKey;
  }

  const label = normalizeWorkflowText(stage?.label);
  const columnMatch = config.columns.find(
    (column) =>
      normalizeWorkflowText(column.label) === label ||
      normalizeWorkflowText(column.key) === label,
  );
  if (columnMatch && CRM_CLIENT_STAGE_ORDER.includes(columnMatch.key as CrmClientWorkflowStageKey)) {
    return columnMatch.key as CrmClientWorkflowStageKey;
  }

  if (label.includes("perdido") || href.includes("perdido")) return "perdido";
  if (label.includes("ganho") || label.includes("fechado") || label.includes("concluido") || href.includes("ganho")) {
    return "ganho";
  }
  if (
    label.includes("negociacao") ||
    label.includes("qualificacao") ||
    label.includes("proposta") ||
    href.includes("em-negociacao") ||
    href.includes("qualificacao") ||
    href.includes("proposta") ||
    href.includes("negociacao")
  ) {
    return "em-negociacao";
  }
  return "lead";
}

export function buildCrmClientWorkflowStages(
  source?: CrmClientWorkflowSource | null,
  config = getDefaultWorkflowConfig("crm"),
): CrmClientWorkflowStage[] {
  const grouped = new Map<CrmClientWorkflowStageKey, CrmClientWorkflowStage>(
    CRM_CLIENT_STAGE_ORDER.map((key) => [key, createCrmClientWorkflowStage(key, 0, config)]),
  );

  const sourceStages = [
    ...(Array.isArray(source?.stagesBefore) ? source.stagesBefore : []),
    ...(Array.isArray(source?.stages) ? source.stages : []),
    ...(Array.isArray(source?.parallelStages) ? source.parallelStages : []),
    ...(Array.isArray(source?.stagesAfter) ? source.stagesAfter : []),
    ...(Array.isArray(source?.finalStages) ? source.finalStages : []),
  ];

  for (const stage of sourceStages) {
    const key = resolveCrmClientWorkflowStageKey(stage, config);
    const current = grouped.get(key);
    if (!current) continue;
    current.value += Number(stage.value || 0);
    if (Array.isArray(stage.items) && stage.items.length > 0) {
      current.items = [...(current.items || []), ...stage.items];
    }
  }

  return CRM_CLIENT_STAGE_ORDER.map((key) => grouped.get(key) as CrmClientWorkflowStage);
}

export function getCrmClientWorkflowStageValue(
  source: CrmClientWorkflowSource | null | undefined,
  key: CrmClientWorkflowStageKey,
  config = getDefaultWorkflowConfig("crm"),
) {
  return buildCrmClientWorkflowStages(source, config).find((stage) => stage.id === key)?.value || 0;
}

export function normalizeOpsDemandStatusKey(
  status?: string,
  config = getDefaultWorkflowConfig("ops"),
) {
  return resolveWorkflowStatus(config, status, OPS_STATUS_ALIASES)?.key;
}

export function getOpsDemandStatusMeta(
  status?: string,
  config = getDefaultWorkflowConfig("ops"),
) {
  const matched = resolveWorkflowStatus(config, status, OPS_STATUS_ALIASES);
  if (matched) {
    return {
      key: matched.key,
      label: matched.label,
      badgeVariant: statusKindToBadgeVariant(matched.kind),
      kind: matched.kind,
    };
  }

  const fallbackLabel = String(status || "—").trim() || "—";
  return {
    key: undefined,
    label: fallbackLabel,
    badgeVariant: "muted" as WorkflowBadgeVariant,
    kind: "unknown",
  };
}

export function isOpsDemandDoneStatus(status?: string, config = getDefaultWorkflowConfig("ops")) {
  const matched = resolveWorkflowStatus(config, status, OPS_STATUS_ALIASES);
  return matched?.countsAsSuccess || matched?.kind === "done";
}

export function isOpsDemandWaitingStatus(status?: string, config = getDefaultWorkflowConfig("ops")) {
  return resolveWorkflowStatus(config, status, OPS_STATUS_ALIASES)?.kind === "waiting";
}

export function isOpsDemandInProgressStatus(status?: string, config = getDefaultWorkflowConfig("ops")) {
  const kind = resolveWorkflowStatus(config, status, OPS_STATUS_ALIASES)?.kind;
  return kind === "in_progress" || kind === "open" || kind === "blocked";
}

export function normalizeOpsDemandPriorityKey(priority?: string): OpsDemandPriorityKey | undefined {
  const normalized = normalizeWorkflowText(priority);
  return (Object.values(OPS_PRIORITY_CONFIG).find((meta) =>
    meta.aliases.some((alias) => normalizeWorkflowText(alias) === normalized),
  )?.key);
}

export function getOpsDemandPriorityMeta(priority?: string) {
  const key = normalizeOpsDemandPriorityKey(priority);
  if (key) return OPS_PRIORITY_CONFIG[key];

  const fallbackLabel = String(priority || "—").trim() || "—";
  return {
    key: undefined,
    label: fallbackLabel,
    badgeVariant: "muted" as WorkflowBadgeVariant,
    dot: "#94a3b8",
    bg: "rgba(148,163,184,0.06)",
    border: "rgba(148,163,184,0.12)",
    level: 0,
  };
}

export function isOpsDemandCriticalPriority(priority?: string) {
  return getOpsDemandPriorityMeta(priority).level >= 4;
}

export function isOpsDemandHighlightedPriority(priority?: string) {
  return getOpsDemandPriorityMeta(priority).level >= 3;
}

export const workflowService = {
  getConfig: async (moduleKey: WorkflowModuleKey) => {
    try {
      const { data } = await api.get(`/v2/workflows/${moduleKey}`);
      return adaptWorkflowConfig(data, moduleKey);
    } catch {
      return getDefaultWorkflowConfig(moduleKey);
    }
  },
};
